import { CloseIcon } from "@chakra-ui/icons";
import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  Icon,
  IconButton,
  Image,
  Progress,
  Stack,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import React, { useState, useRef } from "react";
import { MdUpload } from "react-icons/md";
import { useDispatch } from "react-redux";
import { path } from "../../../utils/Api";
import { actions } from "./redux/slice";
import { IImages } from "../../../views/admin/product/types";
import { useTranslation } from "react-i18next";
import FileSizeExtension from "../../hooks/FileSizeExtension";
import index from "../DateFormat";

type Props = {
  data?: IImages[];
  handleDeleteFile?: (index: number) => void;
  handleUploadImage?: (prefix: string) => void;
  handleUploadComplete?: () => void;
  isRequiredFalse?: boolean;
  title: string;
  hideDelete?: boolean;
  isSingle?: boolean;
};

const MultiFileUpload: React.FC<Props> = ({
  data,
  handleDeleteFile,
  handleUploadImage,
  handleUploadComplete,
  title,
  hideDelete,
  isSingle,
}) => {
  const { t } = useTranslation();
  const bg = useColorModeValue("gray.100", "navy.700");
  const borderColor = useColorModeValue("secondaryGray.100", "whiteAlpha.100");
  const brandColor = useColorModeValue("brand.500", "white");
  const textColor = useColorModeValue("navy.700", "white");
  const [uploadFiles, setUploadFiles] = useState<any[]>([]);
  const [progress, setProgress] = useState(0);
  const dispatch = useDispatch();
  const [dragActive, setDragActive] = useState(false);

  const handleFiles = async (files: FileList | File[]) => {
    const fileList = [...uploadFiles];

    for (const file of Array.from(files)) {
      if (!FileSizeExtension(file, "photo")) continue;

      fileList.push({
        name: file.name,
        url: "",
        prefix: "",
        data: file,
        added: false,
        inprogress: false,
        completed: false,
      });
    }

    setUploadFiles([...fileList]);
    const signedFileItems = [];
    for (let i = 0; i < fileList.length; i++) {
      const item = fileList[i];
      if (!item.completed && !item.inprogress) {
        signedFileItems.push({ name: item.name, index: i });
      }
    }

    const data = await getSignedUrl(signedFileItems.map((x) => x.name));

    for (let i = 0; i < signedFileItems.length; i++) {
      fileList[signedFileItems[i].index].added = true;
      fileList[signedFileItems[i].index].url = data.url[i];
      fileList[signedFileItems[i].index].prefix = data.prefix[i];
    }

    setUploadFiles([...fileList]);

    for (let i = 0; i < fileList.length; i++) {
      const item = fileList[i];
      if (!item.completed && !item.inprogress) {
        fileList[i].inprogress = true;
        setUploadFiles([...fileList]);
        setProgress(0);
        await uploadFile(item.url, item.data);
        setProgress(0);
        fileList[i].completed = true;
        fileList[i].inprogress = false;
        setUploadFiles([...fileList]);
        handleUploadImage && handleUploadImage(fileList[i].prefix);
      }
    }
    handleUploadComplete && handleUploadComplete();
  };

  const handleFileChange = async (evt: React.ChangeEvent<HTMLInputElement>) => {
    if (evt.target.files) {
      handleFiles(evt.target.files);
    }
  };

  const handleDrop = (evt: React.DragEvent<HTMLLabelElement>) => {
    evt.preventDefault();
    if (evt.dataTransfer.files) {
      handleFiles(evt.dataTransfer.files);
    }
  };

  async function getSignedUrl(
    fileNames: string[]
  ): Promise<{ url: string[]; prefix: string[] }> {
    return new Promise((resolve, reject) => {
      try {
        dispatch(
          actions.getSignedMultiUrl({
            fileNames: fileNames,
            callback: (urls: string[], prefixes: string[]) => {
              resolve({ url: urls, prefix: prefixes });
            },
          })
        );
      } catch (error) {
        console.error("Error:", error);
        reject(error);
      }
    });
  }

  async function uploadFile(s3SignedUrl: string, file: File): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", s3SignedUrl, true);
        xhr.setRequestHeader("Content-Type", "application/octet-stream");

        xhr.upload.onprogress = (evt) => {
          if (evt.lengthComputable) {
            const percentComplete = (evt.loaded / evt.total) * 100;
            setProgress(percentComplete);
          }
        };

        xhr.onreadystatechange = () => {
          if (xhr.readyState === 4) {
            if (xhr.status === 200) {
              resolve();
            } else {
              reject(new Error("Failed to upload file to S3"));
            }
          }
        };

        xhr.send(file);
      } catch (error) {
        reject(error);
      }
    });
  }

  return (
    <Stack direction={"column"} spacing="24px">
      <FormControl isRequired={false}>
        <FormLabel fontSize="sm" fontWeight="500" color={textColor} mb="8px">
          {title}
        </FormLabel>
        <FormLabel
          htmlFor={`id-${title + data?.length + 1}`}
          minH={100}
          w={{ base: "100%", "2xl": "268px" }}
          onDrop={(e) => {
            e.preventDefault();
            setDragActive(false);
            handleDrop(e);
          }}
          onDragOver={(e) => {
            e.preventDefault();
            setDragActive(true);
          }}
          onDragEnter={() => setDragActive(true)}
          onDragLeave={() => setDragActive(false)}
        >
          <Flex
            align="center"
            justify="center"
            bg={bg}
            border="1px dashed"
            borderColor={dragActive ? "blue.300" : borderColor}
            borderRadius="16px"
            w="100%"
            minH={120}
            cursor="pointer"
            transition="border-color 0.2s ease"
          >
            <Box textAlign="center">
              <Icon
                as={MdUpload}
                w="40px"
                h="40px"
                color={brandColor}
                mx="auto"
              />
              <Text fontSize="md" fontWeight="700" color={brandColor}>
                {dragActive
                  ? t("common.form.dropHere") || "Drop files here"
                  : t("common.form.uploadFile")}
              </Text>
              <Text fontSize="sm" fontWeight="500" color="secondaryGray.500">
                {t("common.form.fileAllowed")}
              </Text>
            </Box>
          </Flex>
        </FormLabel>
        <input
          type="file"
          accept=".jpg,.png,.jpeg"
          id={`id-${title + data?.length + 1}`}
          onChange={handleFileChange}
          multiple={isSingle ? false : true}
          style={{ display: "none" }}
        />
      </FormControl>

      {uploadFiles.find((x) => x.inprogress) && (
        <Box my={5}>
          <Text>{uploadFiles.find((x) => x.inprogress)?.name}</Text>
          <Progress colorScheme="brand" h="8px" value={progress} />
        </Box>
      )}

      <Box display="flex" alignItems="center" flexWrap="wrap" gap={2}>
        {data?.map((ele, index) => {
          if (ele.isRemove) return null;

          return (
            <Box position="relative" key={index} w="50px" h="50px">
              <Image
                src={path() + ele.file}
                alt={ele.file}
                w="50px"
                h="50px"
                objectFit="cover"
                borderRadius="md"
              />
              {!hideDelete && (
                <IconButton
                  icon={<CloseIcon boxSize={2} />}
                  aria-label="Delete image"
                  position="absolute"
                  bottom="5px"
                  right="3px"
                  h={3}
                  minW={3}
                  bg="red.500"
                  color="white"
                  _hover={{ bg: "red.600" }}
                  onClick={() => handleDeleteFile?.(index)}
                />
              )}
            </Box>
          );
        })}
      </Box>
    </Stack>
  );
};

export default MultiFileUpload;
